import React, { Component } from 'react';
import { ToastContainer, toast } from "react-toastify";
import './notifications.scss'
import 'react-toastify/dist/ReactToastify.css';
import { NotificationEvent, NotificationEventType } from './Events';

type Props = {
    closeToast?: () => void,
    message: string | JSX.Element,
    iconClass?: string
    iconName?: string
}

const BaseToasterMessage = ({ closeToast, message, iconClass, iconName }: Props) => {
    const messageEl = (message instanceof Element) ? message : <p className="message">{message}</p>;
    return (
        <div className="message-container">
            <span className={`material-icons ${iconClass}`}>{iconName}</span>
            {messageEl}
            <button className="close-button" onClick={closeToast}><span className="material-icons close-icon">close</span></button>
        </div>
    )
};

const SuccessMessage = ({ closeToast, message }: Props) => BaseToasterMessage({ closeToast: closeToast, message: message, iconClass: "success", iconName: "check_circle" })

const ErrorMessage = ({ closeToast, message }: Props) => BaseToasterMessage({ closeToast: closeToast, message: message, iconClass: "error", iconName: "error" })

const WarningMessage = ({ closeToast, message }: Props) => BaseToasterMessage({ closeToast: closeToast, message: message, iconClass: "warning", iconName: "warning" })

/**
 * Configurator for the 'beforeunload' event listener, responsible for making a dirt check.
 */
class DirtChecker {

    constructor() {
        window.addEventListener('beforeunload', this._dirtCheck.bind(this));
    }

    /** Operations that are still pending to be ended. */
    _pendingOperations: Set<Promise<any>> = new Set();

    /**
     * Adds an operation promise to the pending operations that clears itself from the set after it's done.
     * @param promise Promise to be added.
     */
    addToPendingOperations(promise: Promise<any>) {
        this._pendingOperations.add(promise);
        const clearFromList = () => this._pendingOperations.delete(promise);
        promise
            .then(clearFromList)
            .catch(clearFromList);
    }

    /**
     * Configures two event listeners that add or resolve a promise of data changed, respectively, for a future dirt check.
     * @param target The target element that the event listeners should be applied to.
     * @param promiseCreatedEventListenerName Event listener name for the event that creates the promise of data changed.
     * @param promiseResolvedEventListenerName Event listener name for the event that resolves the promise of data changed.
     */
    configureEventListeners(target: Element, promiseCreatedEventListenerName: string, promiseResolvedEventListenerName: string) {
        let resolvePendingPromise: any;
        const pendingPromise = new Promise<void>((resolve: any) => resolvePendingPromise = resolve);

        target.addEventListener(promiseCreatedEventListenerName, () => this.addToPendingOperations(pendingPromise));
        target.addEventListener(promiseResolvedEventListenerName, () => resolvePendingPromise(pendingPromise));
    }

    /**
    * Checks if there is any pending operation and, if yes, show message for the user to confirm the unload operation.
    * @param event
    */
    _dirtCheck(event: BeforeUnloadEvent) {
        if (this._pendingOperations.size) {
            event.preventDefault();
            event.returnValue = '';
            return event.returnValue;
        }
    }
}

export default class Notifications extends Component {

    readonly dirtCheck: DirtChecker = new DirtChecker();

    componentDidMount(){
        this.setEventListeners();
    }

    /**
     * Sets the event listeners that show notifications, if necessary.
     */
    setEventListeners() {
        this._setNotificationEventListener();
        this._setPersonalProfileListeners();
    }

    /**
     * Sets the general notification event listener on the document.
     */
    private _setNotificationEventListener() {
        document.addEventListener('notificationEvent', ((event: CustomEvent<NotificationEvent>) => {
            switch (event.detail.eventType) {
                case NotificationEventType.Error:
                    toast(<ErrorMessage message={event.detail.message} />);
                    break;
                case NotificationEventType.Warning:
                    toast(<WarningMessage message={event.detail.message} />);
                    break;
                default:
                    toast(<SuccessMessage message={event.detail.message} />);
                    break;
            }

        }) as EventListener);
    }

    /**
     * Sets the Personal Profile web component event listeners.
     */
    private _setPersonalProfileListeners() {
        const personalProfile = document.querySelector('personal-profile');
        if (personalProfile)
            this.dirtCheck.configureEventListeners(personalProfile, 'changesDone', 'changesCancelled');
    }

    render() {
        return (
            <div className='notification-container'>
                <ToastContainer position="bottom-left"
                    autoClose={5000}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    closeButton={false}
                    hideProgressBar={true}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover />
            </div>
        )
    }
}

